import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { PF2eKnowledgeArticleResponse, PF2eKnowledgeSearchItem } from '../api/client';
import { useTranslation } from '../i18n/provider';
import { usePF2eTranslation } from '../hooks/usePF2eTranslation';
import { EnglishName } from './CharacterWizard/EnglishName';
import SourceBookCard from './CharacterWizard/SourceBookCard';
import RichText from './RichText';
import type { OrcKnowledgeLinkTarget } from './OrcKnowledgeModalContext';
import { Pf2eBadge, type Pf2eRarity } from './ui/Pf2eBadge';
import { knowledgeCategoryLabel } from '../utils/knowledgeDisplay';
import { knowledgeTargetKey } from '../utils/knowledgeRoutes';
import './KnowledgeArticle.css';

interface KnowledgeTitleInput {
  title?: string | null;
  localizedTitle?: string | null;
  fallback: string;
}

interface KnowledgeArticleTitleProps extends KnowledgeTitleInput {
  className?: string;
  englishClassName?: string;
}

interface KnowledgeArticleBodyProps {
  article: PF2eKnowledgeArticleResponse;
  onOrcLinkClick?: (target: OrcKnowledgeLinkTarget) => void;
  showLicense?: boolean;
  sourcePlacement?: 'meta' | 'card' | 'hidden';
}

interface KnowledgeArticleSummaryProps {
  item: PF2eKnowledgeSearchItem;
  onClick?: () => void;
  source?: string | null;
  selected?: boolean;
  to: string;
  footer?: ReactNode;
}

interface KnowledgeSpellRankProps {
  rank?: string | null;
  className?: string;
}

const PF2E_RARITIES: Pf2eRarity[] = ['common', 'uncommon', 'rare', 'unique'];

function knowledgeDisplayTitle({ title, localizedTitle, fallback }: KnowledgeTitleInput): string {
  return localizedTitle || title || fallback;
}

export function KnowledgeSpellRank({ rank, className }: KnowledgeSpellRankProps) {
  const t = useTranslation();
  const normalizedRank = rank?.trim();
  if (!normalizedRank) return null;

  return <span className={className}>{t('spellbook.rank', {}, 'Rank')} {normalizedRank}</span>;
}

function normalizeRarity(value: unknown): Pf2eRarity | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return PF2E_RARITIES.includes(normalized as Pf2eRarity) ? (normalized as Pf2eRarity) : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function articleRarity(article: PF2eKnowledgeArticleResponse): Pf2eRarity | null {
  const metadata = asRecord(article.metadata);
  const system = asRecord(metadata?.system);
  const systemTraits = asRecord(system?.traits);
  const candidates = [
    metadata?.rarity,
    systemTraits?.rarity,
    ...article.traits,
  ];
  for (const candidate of candidates) {
    const rarity = normalizeRarity(candidate);
    if (rarity) return rarity;
  }
  return null;
}

export function KnowledgeArticleTitle({
  title,
  localizedTitle,
  fallback,
  className,
  englishClassName,
}: KnowledgeArticleTitleProps) {
  const displayTitle = knowledgeDisplayTitle({ title, localizedTitle, fallback });

  return (
    <span className={className}>
      {displayTitle}
      {title ? (
        <EnglishName
          englishName={title}
          displayedName={displayTitle}
          className={englishClassName}
        />
      ) : null}
    </span>
  );
}

function sourceText(article: PF2eKnowledgeArticleResponse, pageLabel: string, showLicense: boolean): string | null {
  const source = article.source;
  if (!source?.title && !source?.page && !(showLicense && source?.license)) return null;
  const parts = [
    source?.title,
    source?.page ? `${pageLabel} ${source.page}` : null,
    showLicense ? source?.license : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function KnowledgeArticleBody({
  article,
  onOrcLinkClick,
  showLicense = true,
  sourcePlacement = 'meta',
}: KnowledgeArticleBodyProps) {
  const t = useTranslation();
  const { tRarity, tTrait } = usePF2eTranslation();
  if (article.status !== 'ok') return null;

  const source = sourceText(article, t('knowledge.modal.page', {}, 'p.'), showLicense);
  const showSourceMeta = sourcePlacement === 'meta' && source;
  const showSourceCard = sourcePlacement === 'card' && article.source;
  const rarity = articleRarity(article);
  const traits = article.traits.filter((trait) => normalizeRarity(trait) == null);

  return (
    <article className="knowledge-article">
      <div className="knowledge-article__meta" aria-label={t('knowledge.modal.metadata', {}, 'Article metadata')}>
        {article.category ? <span>{knowledgeCategoryLabel(article.category, t)}</span> : null}
        {showSourceMeta ? <span>{source}</span> : null}
      </div>
      {rarity || traits.length > 0 ? (
        <div className="knowledge-article__traits" aria-label={t('knowledge.modal.traits', {}, 'Traits')}>
          {rarity ? (
            <Pf2eBadge kind="rarity" rarity={rarity}>
              {tRarity(rarity)}
            </Pf2eBadge>
          ) : null}
          {traits.map((trait) => (
            <Pf2eBadge key={trait} trait={trait}>
              {tTrait(trait)}
            </Pf2eBadge>
          ))}
        </div>
      ) : null}
      {article.body_html ? (
        <RichText
          content={article.body_html}
          html
          className="knowledge-article__rich-text"
          onOrcLinkClick={onOrcLinkClick}
        />
      ) : article.summary ? (
        <RichText
          content={article.summary}
          markdown
          className="knowledge-article__rich-text"
          onOrcLinkClick={onOrcLinkClick}
        />
      ) : (
        <p className="knowledge-article__empty">
          {t('knowledge.modal.emptyBody', {}, 'No article body is available yet.')}
        </p>
      )}
      {showSourceCard ? (
        <div className="knowledge-article__source-card">
          <SourceBookCard sourceBook={article.source?.title} sourcePg={article.source?.page} showLicense={showLicense} />
        </div>
      ) : null}
    </article>
  );
}

export function KnowledgeArticleSummary({ item, onClick, source, selected = false, to, footer }: KnowledgeArticleSummaryProps) {
  const t = useTranslation();
  const fallback = knowledgeTargetKey(item.target.type, item.target.slug);
  const category = knowledgeCategoryLabel(item.category || item.target.type, t);

  return (
    <Link
      className={`knowledge-result-card${selected ? ' is-selected' : ''}`}
      to={to}
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
    >
      <div className="knowledge-result-card__body">
        <div className="knowledge-result-card__header">
          <div className="knowledge-result-card__kicker">{category}</div>
          <KnowledgeSpellRank rank={item.rank} className="knowledge-result-card__rank" />
        </div>
        <h3 className="knowledge-result-card__title">
          <KnowledgeArticleTitle
            title={item.title}
            localizedTitle={item.localized_title}
            fallback={fallback}
            englishClassName="knowledge-result-card__english-name"
          />
        </h3>
      </div>
      {source ? <div className="knowledge-result-card__source">{source}</div> : null}
      {footer ? <div className="knowledge-result-card__footer">{footer}</div> : null}
    </Link>
  );
}
